import React, { useEffect } from 'react';
import { Container, Card, VStack, Input, Button } from '@chakra-ui/react';
import { useLoaderData, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import api from '@/utils/api';
import { User, roleOptions } from '@/lib/types';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';

const formSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, { message: 'Email is required' }).email('Invalid email address'),
  role_id: z.string().min(1, 'Role is required'),
});

type FormValues = z.infer<typeof formSchema>;

const EditUser: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const user = useLoaderData() as User;

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await api.put(`workspace/${workspaceId}/users/${user.id}`, data);
      toaster.create({
        type: 'success',
        title: 'User information updated!',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update user',
      });
    }
  };

  useEffect(() => {
    reset({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role_id: user.role_id.toString(),
    });
  }, [user, reset]);

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header textAlign={'center'}>
          <Card.Title fontSize={'3xl'}>Edit {user.first_name}'s info</Card.Title>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={6} align={'flex-start'}>
                <Field
                  label='First name'
                  invalid={!!errors.first_name}
                  errorText={errors.first_name?.message}
                >
                  <Input {...register('first_name')} defaultValue={user.first_name} />
                </Field>
                <Field
                  label='Last name'
                  invalid={!!errors.last_name}
                  errorText={errors.last_name?.message}
                >
                  <Input {...register('last_name')} defaultValue={user.last_name} />
                </Field>
                <Field label='Email' invalid={!!errors.email} errorText={errors.email?.message}>
                  <Input {...register('email')} defaultValue={user.email} type='email' />
                </Field>
                <Field label='Role' invalid={!!errors.role_id} errorText={errors.role_id?.message}>
                  <NativeSelectRoot>
                    <NativeSelectField placeholder={'Select role'} {...register('role_id')}>
                      {roleOptions.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
                <Button
                  colorPalette='brand'
                  type={'submit'}
                  width={'full'}
                  loading={isSubmitting}
                  loadingText='Loading...'
                >
                  Save
                </Button>
              </VStack>
            </form>
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
};

export default EditUser;
